/**
 * Validation for Stripe registry contribution amounts.
 * Used to block saving/proceeding with quantities or dollar amounts that
 * exceed the limits we enforce for fixed-amount and any-amount funds.
 */
import { formatDollars } from "./stripeUtils";
import { ErrorFields } from "./types";

export const MAX_QUANTITY = 100;
export const MAX_FIXED_GIFT_AMOUNT_CENTS = 5_000_000;
export const MAX_ANY_GOAL_AMOUNT_CENTS = 100_000_000;
export const MAX_GUEST_GIFT_AMOUNT_CENTS = 5_000_000;

/**
 * Quantity isn't persisted anywhere (backend has no quantity column, and
 * StripeRegistryDetails doesn't carry it either) — it's reconstructed from
 * the fixed-amount total whenever a raw quantity string isn't available.
 */
export function deriveQuantityFromCents(
  goalAmountCents: number,
  giftAmountCents: number,
): number {
  if (giftAmountCents > 0 && goalAmountCents > 0) {
    return Math.round(goalAmountCents / giftAmountCents);
  }
  return 0;
}

export const isQuantityValid = (quantity: number): boolean =>
  quantity > 0 && quantity <= MAX_QUANTITY;

export const isFixedGiftAmountValid = (cents: number): boolean =>
  cents > 0 && cents <= MAX_FIXED_GIFT_AMOUNT_CENTS;

export const isAnyGoalAmountValid = (cents: number): boolean =>
  cents <= MAX_ANY_GOAL_AMOUNT_CENTS;

export const isGuestGiftAmountValid = (cents: number): boolean =>
  cents <= MAX_GUEST_GIFT_AMOUNT_CENTS;

export const getQuantityErrorMessage = (): string =>
  `Quantity must be greater than 0 and cannot exceed ${MAX_QUANTITY}.`;

export const getFixedGiftAmountErrorMessage = (): string =>
  `Gift amount must be greater than $0 and cannot exceed ${formatDollars(MAX_FIXED_GIFT_AMOUNT_CENTS)}.`;

export const getAnyGoalAmountErrorMessage = (): string =>
  `Total goal cannot exceed ${formatDollars(MAX_ANY_GOAL_AMOUNT_CENTS)}.`;

export const getGuestGiftAmountErrorMessage = (): string =>
  `Gift amount cannot exceed ${formatDollars(MAX_GUEST_GIFT_AMOUNT_CENTS)}.`;

export type StripeAmountConfig = {
  isFixedAmount: boolean;
  giftAmountCents: number;
  goalAmountCents: number;
};

/**
 * Single source of truth for which Stripe amount fields are currently over
 * their limit, given only the persisted/emitted shape. Used identically by
 * EditSection's getErrorsFromDetails (normal editor) and the wizard's
 * Continue handler.
 */
export function getStripeAmountErrorFields(
  config: StripeAmountConfig,
): ErrorFields[] {
  const errors: ErrorFields[] = [];

  if (config.isFixedAmount) {
    const quantity = deriveQuantityFromCents(
      config.goalAmountCents,
      config.giftAmountCents,
    );
    if (!isQuantityValid(quantity)) {
      errors.push(ErrorFields.StripeQuantity);
    }
    if (!isFixedGiftAmountValid(config.giftAmountCents)) {
      errors.push(ErrorFields.StripeGiftAmount);
    }
  } else if (!isAnyGoalAmountValid(config.goalAmountCents)) {
    errors.push(ErrorFields.StripeGoalAmount);
  }

  return errors;
}
